import { For, Show, createSignal } from "solid-js";

import { BulkSubscriptionActionBar } from "~/components/BulkSubscriptionActionBar";
import { ChangePlanDialog } from "~/components/ChangePlanDialog";
import { ScaiControlErrorReference } from "~/components/ScaiControlErrorReference";
import {
  useCancelSubscription,
  useResumeSubscription,
  useSubscriptionHistory,
  useSubscriptions,
  useSuspendSubscription,
} from "~/lib/hooks";
import type { SubscriptionHistoryEntry, SubscriptionRead, SubscriptionState } from "~/lib/types";

const STATE_COLORS: Record<SubscriptionState, string> = {
  trial: "badge-info",
  active: "badge-success",
  past_due: "badge-warning",
  cancelled: "badge-error",
  paused: "badge-ghost",
};

type Props = {
  wsId: () => string;
};

export function SubscriptionPanel(props: Props) {
  const subs = useSubscriptions(props.wsId);
  const [selectedIds, setSelectedIds] = createSignal<Set<string>>(new Set<string>());

  const toggle = (id: string, on: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectedSubs = () => {
    const ids = selectedIds();
    return (subs.data?.items ?? []).filter((s) => ids.has(s.id));
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <h3 class="card-title text-base">Subscriptions</h3>
        <Show when={subs.isLoading}>
          <div class="text-center py-6 opacity-60 text-sm">Loading…</div>
        </Show>
        <Show when={subs.isError}>
          <div class="alert alert-error text-sm">Failed to load subscriptions.</div>
        </Show>
        <Show when={subs.data}>
          {(data) => (
            <Show
              when={data().items.length > 0}
              fallback={
                <div class="text-center py-6 opacity-60 text-sm">
                  This workspace's tenant has no ScaiControl subscription. Profile-B tenants are
                  billed externally; this is fine.
                </div>
              }
            >
              <ul class="flex flex-col gap-3">
                <For each={data().items}>
                  {(s) => (
                    <SubscriptionRow
                      sub={s}
                      wsKey={props.wsId}
                      selected={() => selectedIds().has(s.id)}
                      onToggle={(on) => toggle(s.id, on)}
                    />
                  )}
                </For>
              </ul>
            </Show>
          )}
        </Show>
      </div>
      <Show when={selectedIds().size > 0}>
        <BulkSubscriptionActionBar
          wsKey={props.wsId}
          selected={selectedSubs}
          clearSelection={() => setSelectedIds(new Set<string>())}
        />
      </Show>
    </div>
  );
}

function SubscriptionRow(props: {
  sub: SubscriptionRead;
  wsKey: () => string;
  selected: () => boolean;
  onToggle: (on: boolean) => void;
}) {
  const [showChangePlan, setShowChangePlan] = createSignal(false);
  const [confirmAction, setConfirmAction] = createSignal<null | "cancel" | "suspend" | "resume">(
    null,
  );
  const [actionError, setActionError] = createSignal<string | null>(null);
  const [actionErrorObj, setActionErrorObj] = createSignal<unknown>(null);

  const cancel = useCancelSubscription(props.wsKey);
  const suspend = useSuspendSubscription(props.wsKey);
  const resume = useResumeSubscription(props.wsKey);

  // State-aware action menu. The truth-table:
  //   active / trial    → Change plan · Pause · Cancel (end-of-period)
  //   paused            → Resume
  //   cancelled         → (no actions — ScaiControl rejects mutations on terminal rows)
  //   past_due          → Change plan (rep can downgrade to recover), Cancel
  //   pending cancel    → Resume (treat the row like cancellable-but-still-active)
  const canChangePlan = () => ["active", "trial", "past_due"].includes(props.sub.state);
  const canCancel = () => ["active", "trial", "past_due"].includes(props.sub.state);
  const canSuspend = () => ["active", "trial", "past_due"].includes(props.sub.state);
  const canResume = () =>
    props.sub.state === "paused" || props.sub.pending_cancellation_at !== null;

  const runConfirmed = async () => {
    setActionError(null);
    setActionErrorObj(null);
    const which = confirmAction();
    if (!which) return;
    try {
      if (which === "cancel") {
        await cancel.mutateAsync({
          subId: props.sub.id,
          body: { immediate: false },
        });
      } else if (which === "suspend") {
        await suspend.mutateAsync({ subId: props.sub.id });
      } else if (which === "resume") {
        await resume.mutateAsync({ subId: props.sub.id });
      }
      setConfirmAction(null);
    } catch (err) {
      setActionError((err as Error).message);
      setActionErrorObj(err);
    }
  };

  const isPending = () => cancel.isPending || suspend.isPending || resume.isPending;

  return (
    <li class="border-l-4 border-primary pl-3 py-2">
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div class="flex items-start gap-2">
          <input
            type="checkbox"
            class="checkbox checkbox-sm mt-1"
            checked={props.selected()}
            onChange={(e) => props.onToggle(e.currentTarget.checked)}
            aria-label={`Select ${props.sub.plan_name} for bulk action`}
          />
          <div>
            <div class="font-medium">{props.sub.plan_name}</div>
            <div class="text-xs opacity-60">
              <span class="badge badge-ghost badge-sm mr-1">{props.sub.subscription_kind}</span>
              <code>{props.sub.scaicontrol_subscription_id}</code>
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <span class={`badge badge-sm ${STATE_COLORS[props.sub.state]}`}>{props.sub.state}</span>
          <Show when={canChangePlan() || canCancel() || canSuspend() || canResume()}>
            <div class="dropdown dropdown-end">
              <button
                type="button"
                class="btn btn-ghost btn-xs"
                aria-label={`Actions for ${props.sub.plan_name}`}
              >
                Actions ▾
              </button>
              <ul class="dropdown-content menu menu-sm bg-base-100 rounded-box shadow z-10 w-52 mt-1">
                <Show when={canChangePlan()}>
                  <li>
                    <button type="button" onClick={() => setShowChangePlan(true)}>
                      Change plan…
                    </button>
                  </li>
                </Show>
                <Show when={canSuspend()}>
                  <li>
                    <button type="button" onClick={() => setConfirmAction("suspend")}>
                      Pause subscription
                    </button>
                  </li>
                </Show>
                <Show when={canResume()}>
                  <li>
                    <button type="button" onClick={() => setConfirmAction("resume")}>
                      Resume subscription
                    </button>
                  </li>
                </Show>
                <Show when={canCancel()}>
                  <li>
                    <button
                      type="button"
                      class="text-error"
                      onClick={() => setConfirmAction("cancel")}
                    >
                      Cancel at period end…
                    </button>
                  </li>
                </Show>
              </ul>
            </div>
          </Show>
        </div>
      </div>
      <Show when={props.sub.pending_cancellation_at}>
        <div class="text-xs text-warning mt-1">
          Pending cancellation: {new Date(props.sub.pending_cancellation_at!).toLocaleDateString()}
        </div>
      </Show>
      <Show when={props.sub.payment_status === "past_due"}>
        <div class="text-xs text-error mt-1">Payment past due</div>
      </Show>
      <Show when={props.sub.mrr_amount !== null}>
        <div class="text-xs opacity-70 mt-1">
          MRR: {props.sub.mrr_amount} {props.sub.mrr_currency ?? ""} · quantity:{" "}
          {props.sub.quantity}
        </div>
      </Show>
      <Show when={props.sub.trial_end_date}>
        <div class="text-xs opacity-70">Trial ends: {props.sub.trial_end_date}</div>
      </Show>
      <Show when={props.sub.next_billing_date}>
        <div class="text-xs opacity-70">Next billing: {props.sub.next_billing_date}</div>
      </Show>
      <Show when={props.sub.subscription_kind === "pack" && props.sub.pack_includes}>
        <details class="text-xs opacity-70 mt-1">
          <summary class="cursor-pointer">
            Included services ({props.sub.pack_includes!.length})
          </summary>
          <ul class="mt-1 ml-3 list-disc">
            <For each={props.sub.pack_includes!}>
              {(p) => (
                <li>
                  <code>{p.service_slug}</code> @ <code>{p.plan_id}</code>
                </li>
              )}
            </For>
          </ul>
        </details>
      </Show>

      <ChangeHistorySection sub={props.sub} wsKey={props.wsKey} />

      <Show when={showChangePlan()}>
        <ChangePlanDialog
          wsKey={props.wsKey}
          subscription={props.sub}
          onClose={() => setShowChangePlan(false)}
        />
      </Show>

      <Show when={confirmAction()}>
        <dialog open class="modal modal-open" aria-modal="true">
          <div class="modal-box max-w-md">
            <h3 class="font-bold text-lg mb-2">
              {confirmAction() === "cancel" && "Cancel subscription?"}
              {confirmAction() === "suspend" && "Pause subscription?"}
              {confirmAction() === "resume" && "Resume subscription?"}
            </h3>
            <p class="text-sm opacity-70 mb-4">
              {confirmAction() === "cancel" &&
                "Submits a cancel-at-period-end to ScaiControl. The subscription stays active until the current period ends."}
              {confirmAction() === "suspend" &&
                "Pauses billing immediately. Resume any time to restore."}
              {confirmAction() === "resume" &&
                "Re-activates the subscription. Billing resumes on the next cycle."}
            </p>
            <Show when={actionError()}>
              <div role="alert" class="alert alert-error text-sm mb-3 flex flex-col items-start">
                <span>{actionError()}</span>
                <ScaiControlErrorReference error={actionErrorObj()} />
              </div>
            </Show>
            <div class="modal-action">
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                onClick={() => {
                  setConfirmAction(null);
                  setActionError(null);
                  setActionErrorObj(null);
                }}
                disabled={isPending()}
              >
                Cancel
              </button>
              <button
                type="button"
                class={`btn btn-sm ${confirmAction() === "cancel" ? "btn-error" : "btn-primary"}`}
                onClick={runConfirmed}
                disabled={isPending()}
              >
                {isPending() ? "Submitting…" : "Confirm"}
              </button>
            </div>
          </div>
        </dialog>
      </Show>
    </li>
  );
}

/**
 * Lazy-loaded change history. The query fires only when the rep
 * expands the `<details>` block, so we don't hit ScaiControl for
 * every subscription row in the panel.
 *
 * The inline "Last action by X · 10m ago" summary above the expander
 * loads on first expand too, matching the ScaiControl-recommended
 * UX from the v2 reply §1: "Useful if CRM wants to surface 'Last
 * action by Marcel · 10 min ago' without consuming the webhook stream."
 */
function ChangeHistorySection(props: {
  sub: SubscriptionRead;
  wsKey: () => string;
}) {
  const [opened, setOpened] = createSignal(false);
  const history = useSubscriptionHistory(
    props.wsKey,
    () => props.sub.id,
    () => opened(),
  );
  const items = () => history.data?.items ?? [];
  const newest = () => items()[0] ?? null;

  return (
    <details class="text-xs opacity-70 mt-2" onToggle={(e) => setOpened(e.currentTarget.open)}>
      <summary class="cursor-pointer">Change history</summary>

      <Show when={opened() && history.isLoading}>
        <p class="mt-1 opacity-60">Loading…</p>
      </Show>

      <Show when={opened() && history.isError}>
        <p class="mt-1 text-error">Couldn't load history from ScaiControl.</p>
      </Show>

      <Show when={opened() && history.data && items().length === 0}>
        <p class="mt-1 opacity-60">No recorded changes yet.</p>
      </Show>

      <Show when={opened() && newest()}>
        <p class="mt-1 italic">
          Last: <strong>{newest()!.action ?? "change"}</strong> by{" "}
          <code>{newest()!.user_id ?? "system"}</code>{" "}
          <span class="opacity-60">· {relativeTime(newest()!.created_at)}</span>
        </p>
      </Show>

      <Show when={opened() && items().length > 0}>
        <ol class="mt-2 ml-3 list-decimal flex flex-col gap-1">
          <For each={items()}>{(entry) => <HistoryRow entry={entry} />}</For>
        </ol>
      </Show>
    </details>
  );
}

function HistoryRow(props: { entry: SubscriptionHistoryEntry }) {
  return (
    <li>
      <strong>{props.entry.action ?? "change"}</strong>{" "}
      <span class="opacity-60">
        · by <code>{props.entry.user_id ?? "system"}</code> · {relativeTime(props.entry.created_at)}
      </span>
      <Show when={props.entry.details && Object.keys(props.entry.details).length > 0}>
        <ul class="ml-3 mt-0.5 list-disc opacity-80">
          <For each={Object.entries(props.entry.details ?? {})}>
            {([k, v]) => (
              <li>
                <code>{k}</code>: <code>{String(v)}</code>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </li>
  );
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
